import { MoneySchema } from '@pf/contracts';
import { z } from 'zod';
/**
 * The combined deposit + withdrawal history.
 *
 * There is no PaymentFlow endpoint behind this, and there should not be: PF holds two separate
 * feeds (`GET /v1/transactions`, `GET /v1/withdrawals`) scoped to a *participant*, whereas a
 * customer wants one list scoped to *themselves*, in one order, with the website's own references
 * on it. Joining those is the BFF's job, and this schema is the shape of the answer.
 */
/** One row of the customer's history, spanning both machines. */
export const CustomerActivitySchema = z
    .object({
    /** Which machine this row belongs to — decides how `status` is interpreted. */
    kind: z.enum(['DEPOSIT', 'WITHDRAWAL']),
    /**
     * The id the UI uses to open the detail view: a PaymentFlow transaction id for a `DEPOSIT`,
     * the website's own withdrawal handle for a `WITHDRAWAL`.
     */
    id: z.string().min(1),
    /**
     * A `DepositStatus` or a `CustomerWithdrawalStatus` depending on `kind`. Left as a plain
     * string here because narrowing it per-`kind` would make the row a discriminated union, and
     * every consumer already has `kind` in hand to decide.
     */
    status: z.string().min(1),
    amount: MoneySchema,
    /** Bank on the other side — where the money went, or came from. `null` before a match. */
    counterparty_bank: z.string().min(1).nullable(),
    /** The website's own reference for this row: a client reference or a lock reference. */
    reference: z.string().min(1),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Query parameters `GET /bff/activity` understands. All optional; all filters are ANDed. */
export const CustomerActivityQuerySchema = z
    .object({
    /** `DEPOSIT`, `WITHDRAWAL`, or `ALL`/absent for both. */
    kind: z.string().optional(),
    /** A status value, or `ALL`/absent for any. */
    status: z.string().optional(),
    /** Free-text search over ids and references. */
    q: z.string().optional(),
    /** Opaque cursor from a previous page's `next_cursor`. */
    cursor: z.string().optional(),
})
    .strict();
/** `GET /bff/activity` — one page of history. */
export const CustomerActivityListSchema = z
    .object({
    items: z.array(CustomerActivitySchema),
    /** Cursor for the next page, or `null` at the end. */
    next_cursor: z.string().nullable(),
    /** Total matching rows across all pages, for the "showing N of M" line. */
    total: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=activity.js.map